/**
 * Tags Settings Page
 *
 * Inline editing with auto-save. No modals, no save buttons.
 * - Rounded boxes with 25-50% color shade for tags
 * - Small color picker on click
 * - Debounced auto-save as you type
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Typography,
  Input,
  ColorPicker,
  Popconfirm,
  Spin,
  Empty,
} from 'antd';
import {
  DeleteOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tagsApi } from '@api/index';
import type { Tag as TagType } from '@/types';
import { useAutoSave } from '@hooks/useAutoSave';

const { Title, Text } = Typography;

interface EditableTag extends TagType {
  isNew?: boolean;
}

// Helper to create lighter shade of a color (25-50% opacity effect)
function getLighterColor(hexColor: string, opacity: number = 0.25): string {
  // Remove # if present
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

// Tag badge component with colored background
function TagBadge({ name, color }: { name: string; color: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 10px',
        borderRadius: 12,
        backgroundColor: getLighterColor(color, 0.3),
        color: color,
        fontWeight: 500,
        fontSize: 13,
      }}
    >
      {name || 'Tag name'}
    </span>
  );
}

// Individual tag row with inline editing
function TagRow({
  tag,
  onUpdate,
  onDelete,
  onCreated,
}: {
  tag: EditableTag;
  onUpdate: (id: string, data: Partial<EditableTag>) => Promise<void>;
  onDelete: (id: string) => void;
  onCreated?: (tag: TagType) => void;
}) {
  const [name, setName] = useState(tag.name);
  const [color, setColor] = useState(tag.color || '#1890ff');
  const [isCreating, setIsCreating] = useState(false);
  const inputRef = useRef<any>(null);
  const queryClient = useQueryClient();

  // Auto-save for updates
  const { save, status } = useAutoSave({
    saveFn: async (data: Partial<EditableTag>) => {
      await onUpdate(tag.id, data);
    },
    queryKey: 'tags',
    debounceMs: 600,
  });

  // Create mutation for new tags
  const createMutation = useMutation({
    mutationFn: async (data: { name: string; color: string }) => {
      return tagsApi.create(data);
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      onCreated?.(created);
      // Reset the row for new entry
      setName('');
      setColor('#1890ff');
      setIsCreating(false);
    },
    onError: () => {
      setIsCreating(false);
    },
  });

  // Focus input for new rows
  useEffect(() => {
    if (tag.isNew && inputRef.current) {
      inputRef.current.focus();
    }
  }, [tag.isNew]);

  const handleNameChange = useCallback(
    (value: string) => {
      setName(value);
      if (!tag.isNew && value.trim()) {
        save({ name: value });
      }
    },
    [tag.isNew, save]
  );

  const handleColorChange = useCallback(
    (newColor: any) => {
      const hexColor = typeof newColor === 'string' ? newColor : newColor?.toHexString?.() || '#1890ff';
      setColor(hexColor);
      if (!tag.isNew) {
        save({ color: hexColor });
      }
    },
    [tag.isNew, save]
  );

  const handleNameBlur = useCallback(() => {
    // For new tags, create on blur if name is filled
    if (tag.isNew && name.trim() && !isCreating) {
      setIsCreating(true);
      createMutation.mutate({ name: name.trim(), color });
    }
  }, [tag.isNew, name, color, isCreating, createMutation]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && tag.isNew && name.trim() && !isCreating) {
        setIsCreating(true);
        createMutation.mutate({ name: name.trim(), color });
      }
    },
    [tag.isNew, name, color, isCreating, createMutation]
  );

  // Status indicator
  const StatusIcon = () => {
    if (isCreating || createMutation.isPending) {
      return <SyncOutlined spin style={{ color: '#1890ff', fontSize: 12 }} />;
    }
    switch (status) {
      case 'pending':
      case 'saving':
        return <SyncOutlined spin style={{ color: '#1890ff', fontSize: 12 }} />;
      case 'saved':
        return <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 12 }} />;
      case 'error':
        return <ExclamationCircleOutlined style={{ color: '#ff4d4f', fontSize: 12 }} />;
      default:
        return null;
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 0',
        borderBottom: '1px solid #f0f0f0',
      }}
    >
      {/* Preview badge */}
      <div style={{ minWidth: 120 }}>
        <TagBadge name={name || 'Preview'} color={color} />
      </div>

      {/* Color picker */}
      <ColorPicker
        value={color}
        onChange={handleColorChange}
        size="small"
        presets={[
          {
            label: 'Colors',
            colors: [
              '#1890ff', '#52c41a', '#faad14', '#ff4d4f', '#722ed1',
              '#eb2f96', '#13c2c2', '#fa8c16', '#a0d911', '#2f54eb',
              '#f5222d', '#fa541c', '#fadb14', '#73d13d', '#36cfc9',
            ],
          },
        ]}
      >
        <div
          style={{
            width: 24,
            height: 24,
            borderRadius: 4,
            backgroundColor: color,
            cursor: 'pointer',
            border: '2px solid #fff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            flexShrink: 0,
          }}
        />
      </ColorPicker>

      {/* Name input */}
      <Input
        ref={inputRef}
        value={name}
        onChange={(e) => handleNameChange(e.target.value)}
        onBlur={handleNameBlur}
        onKeyDown={handleKeyDown}
        placeholder={tag.isNew ? 'New tag name...' : 'Tag name'}
        variant="borderless"
        style={{ flex: 1, maxWidth: 300, fontWeight: 500 }}
      />

      {/* Created date */}
      {!tag.isNew && tag.createdAt && (
        <Text type="secondary" style={{ minWidth: 100, fontSize: 12 }}>
          {new Date(tag.createdAt).toLocaleDateString()}
        </Text>
      )}

      {/* Status indicator */}
      <div style={{ width: 20, display: 'flex', justifyContent: 'center' }}>
        <StatusIcon />
      </div>

      {/* Delete button */}
      {!tag.isNew && (
        <Popconfirm
          title="Delete tag?"
          description="This tag will be removed from all templates."
          onConfirm={() => onDelete(tag.id)}
          okText="Delete"
          okButtonProps={{ danger: true }}
        >
          <DeleteOutlined
            style={{
              color: '#999',
              cursor: 'pointer',
              padding: 4,
            }}
          />
        </Popconfirm>
      )}

      {/* Add icon for new row */}
      {tag.isNew && (
        <PlusOutlined style={{ color: '#999', padding: 4 }} />
      )}
    </div>
  );
}

export default function TagsSettingsPage() {
  const queryClient = useQueryClient();

  // Fetch tags
  const { data: tags = [], isLoading } = useQuery({
    queryKey: ['tags'],
    queryFn: () => tagsApi.list(),
  });

  // Update mutation
  const updateTag = useCallback(
    async (id: string, data: Partial<EditableTag>) => {
      await tagsApi.update(id, data);
      queryClient.invalidateQueries({ queryKey: ['tags'] });
    },
    [queryClient]
  );

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => tagsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
    },
  });

  const handleDelete = useCallback(
    (id: string) => {
      deleteMutation.mutate(id);
    },
    [deleteMutation]
  );

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin />
      </div>
    );
  }

  // Create a "new tag" placeholder row
  const newTagPlaceholder: EditableTag = {
    id: 'new',
    tenantId: '',
    name: '',
    color: '#1890ff',
    createdAt: '',
    updatedAt: '',
    isNew: true,
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0, marginBottom: 4 }}>
          Tags
        </Title>
        <Text type="secondary">
          Create tags to label and organize your work packages. Changes save automatically.
        </Text>
      </div>

      {/* Header row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '8px 0',
          borderBottom: '2px solid #f0f0f0',
          color: '#888',
          fontSize: 12,
          fontWeight: 500,
          textTransform: 'uppercase',
        }}
      >
        <div style={{ minWidth: 120 }}>Preview</div>
        <div style={{ width: 24 }}>Color</div>
        <div style={{ flex: 1, maxWidth: 300 }}>Name</div>
        <div style={{ minWidth: 100 }}>Created</div>
        <div style={{ width: 20 }}></div>
        <div style={{ width: 24 }}></div>
      </div>

      {/* Tag rows */}
      {tags.length === 0 && (
        <Empty
          description="No tags yet"
          style={{ padding: '40px 0' }}
        />
      )}

      {tags.map((tag) => (
        <TagRow
          key={tag.id}
          tag={tag}
          onUpdate={updateTag}
          onDelete={handleDelete}
        />
      ))}

      {/* New tag row */}
      <TagRow
        key="new"
        tag={newTagPlaceholder}
        onUpdate={updateTag}
        onDelete={handleDelete}
        onCreated={() => {
          queryClient.invalidateQueries({ queryKey: ['tags'] });
        }}
      />

      <div style={{ marginTop: 16 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Tip: Click the color square to change tag color. Type a name and press Enter or click away to create.
        </Text>
      </div>
    </div>
  );
}
